import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import mapboxgl, { Map as MapboxMap, Marker } from "mapbox-gl";

import { getUserLocation } from "../Services/LocationService";
import { useAuth } from "../Services/AuthContext";
import { formatInitials } from "../utils/format";
import { USER_PIN_Z } from "./Map/pinZIndex";
import { useHasVisited } from "../Hooks/useHasVisited";
import type { MapFeedItemDTO } from "../data/types";

import { useMapFeed } from "./Map/useMapFeed";
import { VenueFloatingCard } from "./Map/VenueFloatingCard";
import MapFeedStatus from "./Map/MapFeedStatus";
import GoingOutCTA from "./GoingOut/GoingOutCTA";
import { clearVenueMarkers, syncVenueMarkers } from "./Map/markerSync";

import "../Styles/Map.css";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken =
  "pk.eyJ1IjoibWFwLXBoaWwiLCJhIjoiY2xncDJhejUyMGJkdzNlbW1xYmp6OWhwcSJ9.PNkpEsE0hi_zfvGDY2af-Q";

const GBG: [number, number] = [11.97456, 57.70887];
const ZOOM_THRESHOLD = 6;
const DEFAULT_ZOOM = 12;

type LngLatTuple = [number, number];

function randomCenter(): LngLatTuple {
  return [(Math.random() - 0.5) * 360, (Math.random() - 0.5) * 100];
}

type SelfPin = { avatarUrl?: string | null; displayName: string };

/**
 * Var du själv är, ritad som din profilbild i stället för en blå prick.
 *
 * Kartan handlar om var människor är — vänners avatarer sitter redan på
 * venue-pinsen, så den egna positionen var det enda anonyma på kartan.
 *
 * z-index på rotelementet av samma skäl som venue-pinsen: Mapbox
 * positionerar det, och man ska aldrig tappa bort sig själv bakom en bar.
 */
function createUserMarkerElement(self: SelfPin | null) {
  const userEl = document.createElement("div");
  userEl.className = "user-pin";
  userEl.style.zIndex = String(USER_PIN_Z);

  updateUserMarkerElement(userEl, self);

  return userEl;
}

function updateUserMarkerElement(userEl: HTMLElement, self: SelfPin | null) {
  const nextKey = self ? `${self.avatarUrl ?? ""}|${self.displayName}` : "";

  // Rör inte DOM:en när inget ändrats — annars laddas bilden om varje gång
  // profilen hämtas om.
  if (userEl.dataset.selfKey === nextKey) return;
  userEl.dataset.selfKey = nextKey;

  userEl.classList.toggle("user-pin--avatar", Boolean(self));

  if (!self) {
    // Utan inloggad profil faller vi tillbaka på pricken. Bättre än en tom
    // ring där ett ansikte borde vara.
    const dot = document.createElement("div");
    dot.className = "user-pin-dot";
    userEl.replaceChildren(dot);
    return;
  }

  userEl.title = "Du är här";

  if (self.avatarUrl) {
    const img = document.createElement("img");
    img.src = self.avatarUrl;
    img.alt = "";
    userEl.replaceChildren(img);
    return;
  }

  const span = document.createElement("span");
  span.textContent = formatInitials(self.displayName);
  userEl.replaceChildren(span);
}

function zoomIsReady(map: MapboxMap) {
  return map.getZoom() >= ZOOM_THRESHOLD;
}

export default function Map() {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const userMarkerRef = useRef<Marker | null>(null);

  const { user } = useAuth();

  /**
   * Profilen kan landa både före och efter att kartan gjort det.
   *
   * Markören skapas inne i map.on("load"), i en effekt utan beroenden, så
   * den kan inte läsa `user` direkt — den hade frusit på värdet vid
   * monteringen, alltså oftast null. Ref:en läses vid skapandet, och
   * effekten längre ned uppdaterar en redan skapad markör.
   */
  const selfPin = useMemo<SelfPin | null>(() => {
    if (!user) return null;

    return {
      avatarUrl: user.avatarUrl ?? null,
      displayName:
        [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email,
    };
  }, [user]);

  const selfPinRef = useRef<SelfPin | null>(selfPin);
  selfPinRef.current = selfPin;

  const venueMarkersRef = useRef<globalThis.Map<string, Marker>>(
    new globalThis.Map()
  );

  const feedByVenueIdRef = useRef<globalThis.Map<string, MapFeedItemDTO>>(
    new globalThis.Map()
  );

const { hasVisited, markVisited } = useHasVisited(
  "beatmap.map.hasVisited.v1"
);

const initialHasVisitedRef = useRef(hasVisited);
const markVisitedRef = useRef(markVisited);

  const [zoomOk, setZoomOk] = useState(false);
  const [selected, setSelected] = useState<MapFeedItemDTO | null>(null);

const feed = useMapFeed(true);
const mapItems = feed.items;

const handleSelectVenue = useCallback((item: MapFeedItemDTO) => {
  setSelected(item);

  const map = mapRef.current;
  const { lat, lon } = item.venue;

  if (!map || typeof lat !== "number" || typeof lon !== "number") {
    return;
  }

  map.flyTo({
    center: [lon, lat],
    zoom: Math.max(map.getZoom(), DEFAULT_ZOOM),
    speed: 1.4,
    essential: true,
  });
}, []);

  useEffect(() => {
    if (!selected) return;

    const updated = mapItems.find(
      (item) => item.venue.id === selected.venue.id
    );

    if (!updated) {
      setSelected(null);
      return;
    }

    if (updated.lastPlayedAt !== selected.lastPlayedAt) {
      setSelected(updated);
    }
  }, [mapItems, selected]);

  useEffect(() => {
  if (mapRef.current || !mapContainer.current) return;

  const hasVisitedInitial = initialHasVisitedRef.current;

  const initialCenter: LngLatTuple = hasVisitedInitial ? GBG : randomCenter();
  const initialZoom = hasVisitedInitial ? DEFAULT_ZOOM : 1;

  const map = new mapboxgl.Map({
    container: mapContainer.current,
    style: "mapbox://styles/map-phil/cmkvdu7w1005301sh1fi260ho",
    center: initialCenter,
    zoom: initialZoom,
    config: {
      basemap: {
        showRoadLabels: false,
      },
    },
  });

  mapRef.current = map;

  map.on("zoomend", () => {
    setZoomOk(zoomIsReady(map));
  });

  setZoomOk(zoomIsReady(map));

  map.on("load", async () => {
    try {
      const { lat, lon } = await getUserLocation();

      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        throw new Error("Invalid location");
      }

      userMarkerRef.current = new mapboxgl.Marker({
        element: createUserMarkerElement(selfPinRef.current),
      })
        .setLngLat([lon, lat])
        .addTo(map);

      if (hasVisitedInitial) {
        map.jumpTo({
          center: [lon, lat],
          zoom: DEFAULT_ZOOM,
        });
      } else {
        map.flyTo({
          center: [lon, lat],
          zoom: DEFAULT_ZOOM,
          speed: 2,
          easing: (t) => t * t,
          essential: true,
        });

        markVisitedRef.current();
      }
    } catch (e) {
      console.warn(
        "Could not get user location, falling back to Gothenburg:",
        e
      );

      if (hasVisitedInitial) {
        map.jumpTo({
          center: GBG,
          zoom: DEFAULT_ZOOM,
        });
      } else {
        map.flyTo({
          center: GBG,
          zoom: DEFAULT_ZOOM,
          essential: true,
        });

        markVisitedRef.current();
      }
    }
  });

  return () => {
    clearVenueMarkers(venueMarkersRef.current, feedByVenueIdRef.current);

    userMarkerRef.current?.remove();
    userMarkerRef.current = null;

    mapRef.current?.remove();
    mapRef.current = null;
  };
}, []);

  // Byter användaren profilbild ska pinnen på kartan följa med, utan att
  // kartan behöver monteras om.
  useEffect(() => {
    const el = userMarkerRef.current?.getElement();
    if (el) updateUserMarkerElement(el, selfPin);
  }, [selfPin]);

  useEffect(() => {
    const map = mapRef.current;

    if (!map) return;

    if (!zoomOk) {
      clearVenueMarkers(venueMarkersRef.current, feedByVenueIdRef.current);
      return;
    }

    syncVenueMarkers({
      map,
      items: mapItems,
      venueMarkers: venueMarkersRef.current,
      feedByVenueId: feedByVenueIdRef.current,
      onSelect: handleSelectVenue,
    });
}, [zoomOk, mapItems, handleSelectVenue]);

return (
  <div className="map-shell">
    <div className="map-container" ref={mapContainer} />

    {/* Ovanpå kartan, inte bredvid den. Kvällsfrågan är en rad — kartan
        och ställena är fortfarande produkten. */}
    <GoingOutCTA />

    {/* Misslyckad hämtning: ligger kvar på senast kända läge med en
        rad om det, eller — utan något att visa — ett eget felläge. */}
    <MapFeedStatus
      error={feed.error}
      hasLoaded={feed.hasLoaded}
      isRetrying={feed.isLoading}
      onRetry={feed.refresh}
    />

    <VenueFloatingCard
      item={selected}
      onClose={() => setSelected(null)}
    />
  </div>
);
}